/**
 * CR456: Tab 2 — Source Mapping (FLYER sources + adapter assignment)
 */
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { RefreshCw, MapPin } from "lucide-react";
import { useFlyerSources } from "./useFlyerSources";
import { useAdapters, type ImageCaptureAdapter } from "./useAdapters";
import { validateMapping } from "@/lib/imageCapture/validateMapping";
import type { FlyerSourceRow } from "@/lib/imageCapture/validateMapping";

type HealthLevel = "OK" | "WARN" | "ERROR";

function HealthBadge({ health }: { health: HealthLevel }) {
  const variants: Record<HealthLevel, string> = {
    OK: "bg-primary text-primary-foreground",
    WARN: "bg-secondary text-secondary-foreground border border-border",
    ERROR: "bg-destructive text-destructive-foreground",
  };
  const labels: Record<HealthLevel, string> = { OK: "✓ OK", WARN: "⚠ WARN", ERROR: "✗ ERROR" };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variants[health]}`}>
      {labels[health]}
    </span>
  );
}

function safeJson(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  return JSON.stringify(val, null, 2);
}

function tryParse(str: string): unknown | null {
  if (!str.trim()) return null;
  try { return JSON.parse(str); } catch { return null; }
}

const NONE = "__none__";

interface SidePanelProps {
  source: FlyerSourceRow | null;
  onClose: () => void;
  onSave: (
    sourceId: string,
    payload: {
      flyer_image_capture_enabled?: boolean;
      flyer_image_capture_adapter_key?: string | null;
      flyer_image_capture_params?: unknown | null;
    }
  ) => Promise<boolean>;
  adapters: ImageCaptureAdapter[];
}

function MappingPanel({ source, onClose, onSave, adapters }: SidePanelProps) {
  const [captureEnabled, setCaptureEnabled] = useState(source?.flyer_image_capture_enabled ?? false);
  const [adapterKey, setAdapterKey] = useState(source?.flyer_image_capture_adapter_key ?? "");
  const [paramsJson, setParamsJson] = useState(safeJson(source?.flyer_image_capture_params));
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState("");

  // Phase 4: reset panel state whenever the selected source changes
  useEffect(() => {
    setCaptureEnabled(source?.flyer_image_capture_enabled ?? false);
    setAdapterKey(source?.flyer_image_capture_adapter_key ?? "");
    setParamsJson(safeJson(source?.flyer_image_capture_params));
    setJsonError("");
  }, [source?.merchant_source_id]);

  const handleAdapterChange = (key: string) => {
    const resolved = key === NONE ? "" : key;
    setAdapterKey(resolved);
    if (resolved && !paramsJson.trim()) {
      const adapter = adapters.find((a) => a.adapter_key === resolved);
      if (adapter?.default_params) {
        setParamsJson(safeJson(adapter.default_params));
      }
    }
  };

  const handleSave = async () => {
    if (!source) return;
    if (paramsJson && !tryParse(paramsJson)) {
      setJsonError("Invalid JSON in adapter params");
      return;
    }
    setJsonError("");
    setSaving(true);
    const ok = await onSave(source.merchant_source_id, {
      flyer_image_capture_enabled: captureEnabled,
      flyer_image_capture_adapter_key: adapterKey || null,
      flyer_image_capture_params: paramsJson ? tryParse(paramsJson) : null,
    });
    setSaving(false);
    if (ok) onClose();
  };

  if (!source) return null;

  const currentResult = validateMapping({
    ...source,
    flyer_image_capture_enabled: captureEnabled,
    flyer_image_capture_adapter_key: adapterKey || null,
    adapter_enabled: adapters.find((a) => a.adapter_key === adapterKey)?.enabled ?? null,
  });

  return (
    <Sheet open={!!source} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{source.merchant_name || source.merchant_id}</SheetTitle>
          <SheetDescription className="font-mono text-xs truncate">{source.source_url}</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-6">
          {source.requires_region && (
            <div className="flex items-center gap-2 text-sm bg-muted rounded-md px-3 py-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>Region required — postal code: <strong>{source.default_postal_code ?? "not set"}</strong></span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Switch checked={captureEnabled} onCheckedChange={setCaptureEnabled} />
            <Label>Enable Image Capture</Label>
          </div>

          <div className="space-y-1">
            <Label>Adapter</Label>
            <Select value={adapterKey || NONE} onValueChange={handleAdapterChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select adapter…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— None —</SelectItem>
                {adapters.map((a) => (
                  <SelectItem key={a.adapter_key} value={a.adapter_key} disabled={!a.enabled}>
                    {a.display_name}
                    {!a.enabled && " (disabled)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {adapterKey && (
              <p className="text-xs text-muted-foreground font-mono">
                {adapters.find((a) => a.adapter_key === adapterKey)?.entrypoint}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Adapter Params (JSON)</Label>
            <Textarea
              value={paramsJson}
              onChange={(e) => { setParamsJson(e.target.value); setJsonError(""); }}
              className="font-mono text-xs min-h-[120px]"
              placeholder="{}"
            />
            {jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Config Health</Label>
              <HealthBadge health={currentResult.health} />
            </div>
            {currentResult.issues.length > 0 && (
              <ul className="space-y-1">
                {currentResult.issues.map((iss, i) => (
                  <li key={i} className={`text-xs flex gap-2 ${iss.level === "ERROR" ? "text-destructive" : "text-muted-foreground"}`}>
                    <span>{iss.level === "ERROR" ? "✗" : "⚠"}</span>
                    <span>{iss.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Mapping"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface SourceMappingTabProps {
  /** CR458: deep-link — auto-open this source's mapping panel */
  preselectedSourceId?: string;
}

export function SourceMappingTab({ preselectedSourceId }: SourceMappingTabProps = {}) {
  const { sources, loading, fetchSources, updateMapping } = useFlyerSources();
  const { adapters } = useAdapters();
  const [selected, setSelected] = useState<FlyerSourceRow | null>(null);
  const [search, setSearch] = useState("");

  // CR458: Auto-open preselected source once sources are loaded
  useEffect(() => {
    if (preselectedSourceId && sources.length > 0 && !selected) {
      const found = sources.find((s) => s.merchant_source_id === preselectedSourceId);
      if (found) setSelected(found);
    }
  }, [preselectedSourceId, sources, selected]);

  const filtered = sources.filter((s) =>
    !search ||
    s.merchant_id.toLowerCase().includes(search.toLowerCase()) ||
    (s.merchant_name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          All FLYER scan sources. Click a row to configure its image capture adapter mapping.
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="Search merchant…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48 h-8 text-sm"
          />
          <Button variant="outline" size="sm" onClick={fetchSources} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Merchant</TableHead>
              <TableHead>Source URL</TableHead>
              <TableHead className="w-[100px]">Region</TableHead>
              <TableHead className="w-[90px]">Capture</TableHead>
              <TableHead>Adapter</TableHead>
              <TableHead className="w-[100px]">Config Health</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No FLYER sources found.</TableCell></TableRow>
            ) : (
              filtered.map((source) => {
                const result = validateMapping(source);
                return (
                  <TableRow
                    key={source.merchant_source_id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setSelected(source)}
                  >
                    <TableCell>
                      <div className="font-medium">{source.merchant_name || source.merchant_id}</div>
                      <div className="text-xs text-muted-foreground">{source.merchant_id}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate" title={source.source_url ?? ""}>
                      {source.source_url || "—"}
                    </TableCell>
                    <TableCell>
                      {source.requires_region ? (
                        <div className="flex items-center gap-1 text-xs">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {source.default_postal_code || <span className="text-muted-foreground">missing</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {source.flyer_image_capture_enabled
                        ? <Badge variant="default" className="text-[10px]">ON</Badge>
                        : <Badge variant="outline" className="text-muted-foreground text-[10px]">OFF</Badge>
                      }
                    </TableCell>
                    <TableCell>
                      {source.adapter_display_name ? (
                        <div>
                          <div className="text-sm">{source.adapter_display_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{source.flyer_image_capture_adapter_key}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <HealthBadge health={result.health} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <MappingPanel
        source={selected}
        onClose={() => setSelected(null)}
        onSave={updateMapping}
        adapters={adapters}
      />
    </div>
  );
}
